import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Box, CircularProgress } from '@material-ui/core';
import TeamLogo from '../atoms/TeamLogo';
import {
  COLOR_BORDER,
  COLOR_ORANGE,
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_MIDDLE_GRAY,
  COLOR_TEXT,
  COLOR_WHITE,
  COLOR_YELLOW,
} from '../styles/colors';
import Player from '../types/Player';
import Team from '../types/Team';
import Dialog from './Dialog';
import FullGameLogTable, { GameLogRow } from './FullGameLogTable';
import { CollegeProductionRow } from '../types/CollegeProductionData';
import gql from '../services/gql';
import { useQuery } from '@tanstack/react-query';
import { PlayerGameLog } from '../types/PlayerGameLog';

export interface CollegeYearEntry {
  year: number;
  team?: Team;
  teamName: string;
  jerseyNumber?: string;
  starts: number;
  games: number;
  snaps: number;
  mostSnaps?: boolean;
}

interface CollegeCareerOverviewProps {
  className?: string;
  player?: Player;
  fullGameLogHref?: string;
  collegeProductionData: CollegeProductionRow[];
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: theme.spacing(1),
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
      height: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: COLOR_MIDDLE_GRAY,
      borderRadius: 4,
    },
  },
  scrollRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    minWidth: 'min-content',
    padding: theme.spacing(2, 0),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 0),
    },
  },
  card: {
    flexShrink: 0,
    minWidth: 200,
    maxWidth: 280,
    width: 260,
    backgroundColor: COLOR_WHITE,
    borderRadius: 12,
    border: `1px solid ${COLOR_BORDER}`,
    boxSizing: 'border-box',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      minWidth: 180,
      width: 220,
    },
  },
  summaryCard: {
    border: `2px solid ${COLOR_ORANGE}`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${COLOR_BORDER}`,
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
    color: COLOR_TEXT,
  },
  yearHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  yearBadge: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
    color: COLOR_TEXT,
  },
  teamLogo: {
    height: 24,
    width: 'auto',
  },
  teamLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: COLOR_TEXT,
  },
  fullGameLogLink: {
    fontSize: theme.typography.pxToRem(13),
    color: COLOR_BLUE,
    textDecoration: 'underline',
    cursor: 'pointer',
    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },
  cardBody: {
    display: 'flex',
    alignItems: 'stretch',
  },
  statColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2, 1),
    borderRight: `1px solid ${COLOR_BORDER}`,
    '&:last-child': {
      borderRight: 'none',
    },
  },
  statValue: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(22),
    color: COLOR_TEXT,
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: theme.typography.pxToRem(12),
    color: COLOR_TEXT,
    marginTop: theme.spacing(0.25),
  },
  mostSnapsBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: COLOR_YELLOW,
    color: COLOR_TEXT,
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 700,
    padding: theme.spacing(0.25, 1),
    borderRadius: 8,
    boxShadow: `0 1px 4px ${COLOR_BORDER}`,
  },
  yearCardWrapper: {
    position: 'relative',
  },
  fullGameLogDialogContent: {
    maxWidth: 1100,
    width: '96%',
  },
  gameLogLoaderWrapper: {
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gameLogEmptyState: {
    minHeight: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLOR_TEXT,
    fontSize: theme.typography.pxToRem(14),
  },
}));

// const DEFAULT_CAREER = { starts: 27, games: 24, snaps: 896 };

export default function CollegeCareerOverview(props: CollegeCareerOverviewProps) {
  const {
    className,
    player,
    fullGameLogHref,
    collegeProductionData,
  } = props;

  const [isFullGameLogDialogOpen, setIsFullGameLogDialogOpen] = useState<boolean>(false);
  const classes = useStyles();

  const { data: playerGameLogData, isLoading: isPlayerGameLogLoading } = useQuery<PlayerGameLog[]>(
    ['playerGameLog', player?.id],
    () => gql(`
      getPlayerSSAGameLog(playerId: ${player?.id}) {
        gameId
        playerId
        teamId
        opponentTeamId
        hasStarted
        passComp
        passAtt
        passYard
        passTD
        passInt
        rushAtt
        rushYard
        rushTD
        rec
        recYards
        recTD
        tackleSolo
        tackleAssist
        sack
        tackleForLoss
        intRet
        fumbleForced
        fumRet
        createdAt
        updatedAt
        opponentTeam {
            name
          }
        teamScore
        opponentTeamScore
      }
    `).then((data: any) => (data?.getPlayerSSAGameLog || []) as PlayerGameLog[]),
    {
      enabled: !!player?.id,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    },
  );

  const fullGameLogRows: GameLogRow[] = useMemo(() => {
    if (!playerGameLogData || playerGameLogData.length === 0) return [];

    return playerGameLogData.map((row, idx) => {
      const createdDate = row.createdAt ? new Date(row.createdAt) : null;
      const formattedDate = createdDate && !Number.isNaN(createdDate.getTime())
        ? `${String(createdDate.getMonth() + 1).padStart(2, '0')}/${String(createdDate.getDate()).padStart(2, '0')}/${String(createdDate.getFullYear()).slice(-2)}`
        : '--/--/--';
      const rushAtt = Number(row.rushAtt) || 0;
      const rushYards = Number(row.rushYard) || 0;
      const ypa = rushAtt > 0 ? Math.round((rushYards / rushAtt) * 10) / 10 : 0;

      const rec = Number(row.rec) || 0;
      const recYards = Number(row.recYards) || 0;
      const ypc = rec > 0 ? Math.round((recYards / rec) * 10) / 10 : 0;

      return {
        id: String(row.gameId || idx),
        date: formattedDate,
        opponent: row.opponentTeam?.name || 'Unknown',
        opponentHref: row?.opponentTeam?.name ? `/team/${row.opponentTeam.name.toLowerCase().replace(/\s+/g, '-')}` : undefined,
        score: (row.teamScore != null && row.opponentTeamScore != null) ? `${row.teamScore}-${row.opponentTeamScore} ${row.teamScore == row.opponentTeamScore ? 'D' : (row.teamScore > row.opponentTeamScore) ? 'W': "L" }` : '',
        passing: {
          comp: Number(row.passComp) || 0,
          att: Number(row.passAtt) || 0,
          ints: Number(row.passInt) || 0,
          yds: Number(row.passYard) || 0,
          tds: Number(row.passTD) || 0,
        },
        rushing: {
          att: rushAtt,
          yds: rushYards,
          td: Number(row.rushTD) || 0,
          ypa,
        },
        receiving: {
          rec: Number(row.rec) || 0,
          yds: Number(row.recYards) || 0,
          tds: Number(row.recTD) || 0,
          ypc,
        },
        defense: {
          tkls: Number(row.tackleSolo) || 0,
          sacks: Number(row.sack) || 0,
          tfls: Number(row.tackleForLoss) || 0,
          ints: Number(row.intRet) || 0,
          ffs: Number(row.fumbleForced) || 0,
          frs: Number(row.fumRet) || 0,
        },
      };
    });
  }, [playerGameLogData]);

  const collegeCareerOverviewBase: CollegeYearEntry[] = collegeProductionData.sort((a, b) => b.year - a.year).map((row, index) => ({
    year: row.year,
    teamName: row.teamName,
    jerseyNumber: '45',//row?.jerseyNumber,
    starts: row?.gamesStarted,
    games: row?.gamesPlayed,
    snaps: row?.snaps,
    mostSnaps: false,
  }));

  const maxSnaps = collegeCareerOverviewBase.reduce(
    (maxValue, entry) => Math.max(maxValue, Number(entry.snaps) || 0),
    0,
  );

  const collegeCareerOverview: CollegeYearEntry[] = collegeCareerOverviewBase.map(entry => ({
    ...entry,
    mostSnaps: (Number(entry.snaps) || 0) === maxSnaps,
  }));

  const totals = collegeCareerOverview.length ? {
    starts: collegeCareerOverview.reduce((total, entry) => total + (Number(entry.starts) || 0), 0), 
    games: collegeCareerOverview.reduce((total, entry) => total + (Number(entry.games) || 0), 0),
    snaps: collegeCareerOverview.reduce((total, entry) => total + (Number(entry.snaps) || 0), 0),
  } : { starts: 0, games: 0, snaps: 0 };

  const years = collegeCareerOverview;

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.scrollRow}>
        {/* Summary card */}
        <div className={clsx(classes.card, classes.summaryCard)}>
          <div className={classes.cardHeader}>
            <span className={classes.cardTitle}>College Career</span>
            {
              playerGameLogData?.length ?
                fullGameLogHref != null ? (
                  <a
                    href={fullGameLogHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.fullGameLogLink}
                  >
                    Full Game Log
                  </a>
                ) : (
                  <span onClick={() => {setIsFullGameLogDialogOpen(true)}} className={classes.fullGameLogLink}>Full Game Log</span>
                )
              : null
            }
          </div>
          <div className={classes.cardBody}>
            <div className={classes.statColumn}>
              <span className={classes.statValue}>{totals.starts}</span>
              <span className={classes.statLabel}>Starts</span>
            </div>
            <div className={classes.statColumn}>
              <span className={classes.statValue}>{totals.games}</span>
              <span className={classes.statLabel}>Games</span>
            </div>
            <div className={classes.statColumn}>
              <span className={classes.statValue}>{totals.snaps}</span>
              <span className={classes.statLabel}>Snaps</span>
            </div>
          </div>
        </div>

        {/* Year cards */}
        {years.map((entry, idx) => (
          <div key={idx} className={classes.yearCardWrapper}>
            {entry.mostSnaps && (
              <span className={classes.mostSnapsBadge}>Most Snaps</span>
            )}
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.yearHeader}>
                  <span className={classes.yearBadge}>{entry.year}</span>
                  {entry.team && (
                    <TeamLogo team={entry.team} className={classes.teamLogo} />
                  )}
                  <span className={classes.teamLabel}>
                    {entry.teamName}
                    {entry.jerseyNumber != null && entry.jerseyNumber !== '' && ` - #${entry.jerseyNumber}`}
                  </span>
                </div>
              </div>
              <div className={classes.cardBody}>
                <div className={classes.statColumn}>
                  <span className={classes.statValue}>{entry.starts}</span>
                  <span className={classes.statLabel}>Starts</span>
                </div>
                <div className={classes.statColumn}>
                  <span className={classes.statValue}>{entry.games}</span>
                  <span className={classes.statLabel}>Games</span>
                </div>
                <div className={classes.statColumn}>
                  <span className={classes.statValue}>{entry.snaps}</span>
                  <span className={classes.statLabel}>Snaps</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog
        open={isFullGameLogDialogOpen}
        title="Full Game Log"
        onClose={() => setIsFullGameLogDialogOpen(false)}
        containsLongContent
        contentClassName={classes.fullGameLogDialogContent}
      >
        {isPlayerGameLogLoading ? (
          <Box className={classes.gameLogLoaderWrapper}>
            <CircularProgress size={28} />
          </Box>
        ) : fullGameLogRows.length > 0 ? (
          <FullGameLogTable useNamedBox={false} rows={fullGameLogRows} />
        ) : (
          <Box className={classes.gameLogEmptyState}>No game log data available.</Box>
        )}
      </Dialog>
    </div>
  );
}
